import React, { useState, useEffect } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";

const API = process.env.REACT_APP_WHATSAPP_API || 'https://whatsapp.socialpartners.in/api';

function WaContacts() {
    const [contacts, setContacts] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTag, setActiveTag] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editContact, setEditContact] = useState(null);
    const [form, setForm] = useState({ name: '', phone: '', email: '', tags: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [toast, setToast] = useState(null);
    const [importingLeads, setImportingLeads] = useState(false);
    const [cleaningFailed, setCleaningFailed] = useState(false);
    const [updatingTags, setUpdatingTags] = useState(false);

    useEffect(() => {
        fetchContacts();
        fetchTags();
    }, []);

    const showToast = (text, type = 'success') => {
        setToast({ text, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchContacts = async (q = '', tag = '') => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (q) params.append('search', q);
            if (tag) params.append('tag', tag);
            const res = await fetch(`${API}/wa-contacts?${params}`);
            const data = await res.json();
            setContacts(data.data || []);
        } catch (err) {
            showToast('Failed to load contacts', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchTags = async () => {
        try {
            const res = await fetch(`${API}/wa-contacts/tags`);
            const data = await res.json();
            setTags(data.data || []);
        } catch (err) { }
    };

    const openAdd = () => {
        setEditContact(null);
        setForm({ name: '', phone: '', email: '', tags: '', notes: '' });
        setShowModal(true);
    };

    const openEdit = (c) => {
        setEditContact(c);
        setForm({
            name: c.name || '',
            phone: c.phone || '',
            email: c.email || '',
            tags: c.tags || '',
            notes: c.notes || '',
        });
        setShowModal(true);
    };

    // ─── Clean contacts whose number is not on WhatsApp ───
    // Rule: delete contact if —
    //   • it has at least 1 message in the messages table for that phone (the `to` field)
    //   • AND there is NO inbound message at all (direction = 'inbound')
    //   • AND every outbound message has status = 'failed'
    // Keep contact if:
    //   • zero messages ever (new contact — never messaged)
    //   • any inbound message exists (real WA number, someone replied)
    //   • any outbound message has status = sent / delivered / read / received
    const cleanFailedContacts = async () => {
        const confirm1 = await Swal.fire({
            title: 'Clean Invalid WA Numbers?',
            html: `
                <div style="text-align:left;font-size:14px;line-height:1.9;color:#555">
                    <b>Will DELETE</b> contacts where:<br/>
                    &nbsp;&nbsp;• At least 1 outbound message was attempted, AND<br/>
                    &nbsp;&nbsp;• <b>All</b> outbound messages have status <b>failed</b>, AND<br/>
                    &nbsp;&nbsp;• There is <b>no inbound message</b> from them<br/><br/>
                    <b style="color:#128C7E">Will KEEP</b> contacts where:<br/>
                    &nbsp;&nbsp;• Never messaged (new contact)<br/>
                    &nbsp;&nbsp;• Even 1 message was sent/delivered/read<br/>
                    &nbsp;&nbsp;• Even 1 inbound message received from them
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Analyse & Clean',
            cancelButtonText: 'Cancel',
        });

        if (!confirm1.isConfirmed) return;

        setCleaningFailed(true);

        Swal.fire({
            title: 'Fetching all messages...',
            text: 'Loading message history in one batch — this may take a moment.',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            // Fetch ALL messages in one shot and group by `to` field client-side.
            // Individual per-contact fetches returned empty because the backend
            // likely doesn't support ?to= filtering — this is the reliable approach.
            const [allContactsRes, allMsgsRes] = await Promise.all([
                fetch(`${API}/wa-contacts`),
                fetch(`${API}/messages?limit=10000`),
            ]);

            const allContacts = (await allContactsRes.json()).data || [];
            const msgsRaw = await allMsgsRes.json();
            const allMessages = msgsRaw.data || msgsRaw || [];

            // Group messages by the `to` field (the recipient phone number)
            // For inbound messages, the recipient is our own number — use `from` as the contact phone
            const msgsByPhone = {}; // phone → [messages]
            for (const msg of allMessages) {
                // outbound: to = contact phone
                // inbound:  from = contact phone
                const contactPhone = msg.direction === 'inbound' ? msg.from : msg.to;
                if (!contactPhone) continue;
                if (!msgsByPhone[contactPhone]) msgsByPhone[contactPhone] = [];
                msgsByPhone[contactPhone].push(msg);
            }

            const toDelete = [];
            const debugRows = []; // for showing in dialog

            for (const contact of allContacts) {
                const messages = msgsByPhone[contact.phone] || [];

                // Zero messages → new contact, keep it
                if (!messages.length) continue;

                // Any inbound → real WA number, keep
                const hasInbound = messages.some(m => m.direction === 'inbound');
                if (hasInbound) continue;

                // Outbound only — check statuses
                const outbound = messages.filter(m => m.direction === 'outbound');
                if (!outbound.length) continue;

                const successStatuses = ['sent', 'delivered', 'read', 'received'];
                const hasSuccess = outbound.some(m => successStatuses.includes(m.status));
                if (hasSuccess) continue;

                // All outbound failed, no inbound → not on WhatsApp
                toDelete.push({ id: contact.id, phone: contact.phone, name: contact.name });
                debugRows.push(`${contact.name || contact.phone} — ${outbound.length} failed msg(s)`);
            }

            Swal.close();

            if (toDelete.length === 0) {
                Swal.fire({
                    icon: 'success',
                    title: 'All Clean!',
                    text: 'No contacts found with only failed messages. Nothing to delete.',
                });
                return;
            }

            // Show preview before final delete
            const previewHtml = toDelete.slice(0, 10).map((c, i) =>
                `<div style="padding:3px 0;font-size:13px;color:#374151">
                    • <b>${c.name || '—'}</b>
                    <span style="color:#9ca3af;font-family:monospace;font-size:11px;margin-left:6px">${c.phone}</span>
                    <span style="color:#ef4444;font-size:11px;margin-left:4px">(${debugRows[i] ? debugRows[i].split('—')[1].trim() : ''})</span>
                </div>`
            ).join('') + (toDelete.length > 10
                ? `<div style="font-size:12px;color:#9ca3af;margin-top:6px">...and ${toDelete.length - 10} more</div>`
                : '');

            const confirm2 = await Swal.fire({
                title: `Delete ${toDelete.length} contact${toDelete.length > 1 ? 's' : ''}?`,
                html: `
                    <div style="text-align:left">
                        <p style="font-size:13px;color:#6b7280;margin-bottom:10px">
                            These numbers had only failed outbound messages and never replied — they are likely not on WhatsApp:
                        </p>
                        <div style="max-height:220px;overflow-y:auto;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:10px 14px">
                            ${previewHtml}
                        </div>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                confirmButtonText: `Delete ${toDelete.length} Contact${toDelete.length > 1 ? 's' : ''}`,
                cancelButtonText: 'Cancel',
            });

            if (!confirm2.isConfirmed) return;

            // Perform deletion
            let deleted = 0;
            let errors = 0;
            for (const c of toDelete) {
                try {
                    const delRes = await fetch(`${API}/wa-contacts/${c.id}`, { method: 'DELETE' });
                    if (delRes.ok) deleted++;
                    else errors++;
                } catch { errors++; }
            }

            Swal.fire({
                icon: 'success',
                title: 'Cleanup Complete',
                html: `
                    <div style="text-align:left;font-size:14px;line-height:2">
                        🗑️ <b>Deleted:</b> ${deleted} contact${deleted !== 1 ? 's' : ''}<br/>
                        ${errors > 0 ? `⚠️ <b>Failed to delete:</b> ${errors}` : '✅ No errors'}
                    </div>
                `,
            });

            fetchContacts(search, activeTag);
            fetchTags();

        } catch (err) {
            Swal.close();
            Swal.fire({ icon: 'error', title: 'Error', text: err.message });
        } finally {
            setCleaningFailed(false);
        }
    };

    // ─── Update WA Contact tags from Leads using Sub Industry ───
    const updateTagsFromLeads = async () => {
        const confirm1 = await Swal.fire({
            title: 'Sync Tags from Leads?',
            html: `
                <div style="text-align:left;font-size:14px;line-height:1.8;color:#555">
                    For each WA Contact that matches a Lead (by phone):<br/>
                    • Adds the lead&#39;s <b>Sub Industry</b> as a tag<br/>
                    • Existing tags are <b>preserved</b><br/>
                    • Matches by last 10 digits (handles country code differences)
                </div>
            `,
            icon: 'info',
            showCancelButton: true,
            confirmButtonColor: '#128C7E',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Sync Tags',
            cancelButtonText: 'Cancel',
        });

        if (!confirm1.isConfirmed) return;

        setUpdatingTags(true);
        Swal.fire({ title: 'Fetching data...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

        try {
            // Fetch leads and WA contacts in parallel
            const [leadsResponse, waRes] = await Promise.all([
                axiosInstance.get("/leads/"),
                fetch(`${API}/wa-contacts`),
            ]);

            const leads = leadsResponse.data.data || leadsResponse.data || [];
            const waData = await waRes.json();
            const waContacts = waData.data || [];

            if (!leads.length) {
                Swal.fire({ icon: 'warning', title: 'No leads found' });
                return;
            }

            // Normalize: digits only, then last 10 for matching
            // Handles: 916374646057, 6374646057, +91-637-464-6057, 91 6374646057
            const digitsOnly = (p) => String(p || '').replace(/\D/g, '');
            const last10 = (p) => digitsOnly(p).slice(-10);

            // Diagnostics
            let leadsNoPhone = 0;
            let leadsNoSubIndustry = 0;

            // Build last10 → sub_industry map
            const leadMap = {};
            for (const lead of leads) {
                const rawPhone = lead.whatsapp_no || lead.phone;
                if (!rawPhone || digitsOnly(rawPhone).length < 7) { leadsNoPhone++; continue; }
                if (!lead.sub_industry || !String(lead.sub_industry).trim()) { leadsNoSubIndustry++; continue; }
                const key = last10(rawPhone);
                if (!leadMap[key]) leadMap[key] = String(lead.sub_industry).trim();
            }

            // Match WA contacts to leads
            const toUpdate = [];
            let alreadyHasTag = 0;
            let noLeadMatch = 0;

            for (const contact of waContacts) {
                const key = last10(contact.phone);
                const subIndustry = leadMap[key];
                if (!subIndustry) { noLeadMatch++; continue; }

                const currentTags = (contact.tags || '').split(',').map(t => t.trim()).filter(Boolean);
                if (currentTags.includes(subIndustry)) { alreadyHasTag++; continue; }

                toUpdate.push({
                    contact,
                    newTags: [...currentTags, subIndustry].join(', '),
                    addedTag: subIndustry,
                });
            }

            Swal.close();

            if (toUpdate.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'Nothing to Update',
                    html: `
                        <div style="text-align:left;font-size:13px;line-height:2;color:#555">
                            ✅ <b>Already have tag:</b> ${alreadyHasTag} contacts<br/>
                            🔍 <b>No matching lead:</b> ${noLeadMatch} contacts<br/>
                            ⚠️ <b>Leads with no phone:</b> ${leadsNoPhone}<br/>
                            ⚠️ <b>Leads with no sub industry:</b> ${leadsNoSubIndustry}
                        </div>
                    `,
                });
                return;
            }

            const previewHtml = toUpdate.slice(0, 8).map(u =>
                `<div style="padding:4px 0;font-size:13px;color:#374151">
                    • <b>${u.contact.name || '—'}</b>
                    <span style="color:#9ca3af;font-size:11px;margin-left:4px">${u.contact.phone}</span>
                    → <b style="color:#128C7E">+${u.addedTag}</b>
                </div>`
            ).join('') + (toUpdate.length > 8
                ? `<div style="font-size:12px;color:#9ca3af;margin-top:6px">...and ${toUpdate.length - 8} more</div>`
                : '');

            const confirm2 = await Swal.fire({
                title: `Update ${toUpdate.length} contact${toUpdate.length !== 1 ? 's' : ''}?`,
                html: `
                    <div style="text-align:left">
                        <div style="max-height:220px;overflow-y:auto;background:#f0faf8;border:1px solid #b2dfdb;border-radius:8px;padding:10px 14px;margin-bottom:10px">
                            ${previewHtml}
                        </div>
                        <div style="font-size:12px;color:#9ca3af;line-height:1.8">
                            Already up to date: ${alreadyHasTag} &nbsp;·&nbsp; No lead match: ${noLeadMatch}
                            ${leadsNoSubIndustry > 0 ? ` &nbsp;·&nbsp; Missing sub industry: ${leadsNoSubIndustry}` : ''}
                        </div>
                    </div>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#128C7E',
                confirmButtonText: `Update ${toUpdate.length} Contact${toUpdate.length !== 1 ? 's' : ''}`,
                cancelButtonText: 'Cancel',
            });

            if (!confirm2.isConfirmed) return;

            Swal.fire({ title: 'Updating tags...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

            let updated = 0;
            let errors = 0;
            for (const { contact, newTags } of toUpdate) {
                try {
                    const res = await fetch(`${API}/wa-contacts/${contact.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ tags: newTags }),
                    });
                    if (res.ok) updated++; else errors++;
                } catch { errors++; }
            }

            Swal.fire({
                icon: 'success',
                title: 'Tags Synced',
                html: `
                    <div style="text-align:left;font-size:14px;line-height:2">
                        ✅ <b>Updated:</b> ${updated} contacts<br/>
                        ⏭ <b>Already had tag:</b> ${alreadyHasTag}<br/>
                        🔍 <b>No lead match:</b> ${noLeadMatch}<br/>
                        ${leadsNoSubIndustry > 0 ? `⚠️ <b>Leads missing sub industry:</b> ${leadsNoSubIndustry}<br/>` : ''}
                        ${errors > 0 ? `❌ <b>Errors:</b> ${errors}` : ''}
                    </div>
                `,
            });

            fetchContacts(search, activeTag);
            fetchTags();

        } catch (err) {
            Swal.close();
            Swal.fire({ icon: 'error', title: 'Error', text: err.message });
        } finally {
            setUpdatingTags(false);
        }
    };

    const importLeadsToWhatsApp = async () => {
        if (!window.confirm('Import leads to WA Contacts? This will check each number on WhatsApp first.')) return;

        try {
            const response = await axiosInstance.get("/leads/");
            const leads = response.data.data || response.data || [];

            if (!leads.length) {
                Swal.fire({ icon: 'warning', title: 'No leads found' });
                return;
            }

            const contacts = leads
                .filter(l => l.whatsapp_no || l.phone)
                .map(l => ({
                    name: l.company_name || null,
                    phone: l.whatsapp_no || l.phone,
                    email: l.email || null,
                    tags: 'leads',
                }));

            Swal.fire({
                title: 'Importing...',
                text: `Checking ${contacts.length} numbers on WhatsApp. This may take a moment.`,
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading(),
            });

            const waRes = await fetch('https://whatsapp.socialpartners.in/api/wa-contacts/bulk-import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contacts }),
            });

            const waData = await waRes.json();

            Swal.fire({
                icon: 'success',
                title: 'Import Complete',
                html: `
        <div style="text-align:left;font-size:14px;line-height:2">
          ✅ <b>Imported:</b> ${waData.results.imported}<br/>
          ⏭ <b>Already existed:</b> ${waData.results.already}<br/>
          ❌ <b>Not on WhatsApp:</b> ${waData.results.no_whatsapp}<br/>
          ⚠️ <b>Invalid numbers:</b> ${waData.results.skipped}
        </div>
      `,
            });

        } catch (err) {
            console.error('Import error:', err);
            Swal.fire({ icon: 'error', title: 'Import Failed', text: err.message });
        }
    };

    const saveContact = async () => {
        if (!form.phone) { showToast('Phone number is required', 'error'); return; }
        setSaving(true);
        try {
            const url = editContact
                ? `${API}/wa-contacts/${editContact.id}`
                : `${API}/wa-contacts`;
            const method = editContact ? 'PUT' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Save failed');
            showToast(editContact ? 'Contact updated' : 'Contact added');
            setShowModal(false);
            fetchContacts(search, activeTag);
            fetchTags();
        } catch (err) {
            showToast(err.message, 'error');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c) => {
        try {
            await fetch(`${API}/wa-contacts/${c.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isActive: !c.isActive }),
            });
            fetchContacts(search, activeTag);
        } catch (err) {
            showToast('Update failed', 'error');
        }
    };

    const deleteContact = async (id) => {
        if (!window.confirm('Delete this contact?')) return;
        try {
            await fetch(`${API}/wa-contacts/${id}`, { method: 'DELETE' });
            showToast('Contact deleted');
            fetchContacts(search, activeTag);
            fetchTags();
        } catch (err) {
            showToast('Delete failed', 'error');
        }
    };

    const importFromConversations = async () => {
        setImporting(true);
        try {
            const res = await fetch(`${API}/wa-contacts/import`, { method: 'POST' });
            const data = await res.json();
            showToast(data.message || 'Imported successfully');
            fetchContacts(search, activeTag);
            fetchTags();
        } catch (err) {
            showToast('Import failed', 'error');
        } finally {
            setImporting(false);
        }
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        fetchContacts(e.target.value, activeTag);
    };

    const handleTagFilter = (tag) => {
        const newTag = activeTag === tag ? '' : tag;
        setActiveTag(newTag);
        fetchContacts(search, newTag);
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const str = String(name);
        if (/^\d+$/.test(str)) return str.slice(-2);
        return str.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    const avatarColors = [
        ['#e8f5e9', '#1b5e20'], ['#e3f2fd', '#0d47a1'],
        ['#f3e5f5', '#4a148c'], ['#fff8e1', '#e65100'],
        ['#fce4ec', '#880e4f'], ['#e0f7fa', '#006064'],
    ];

    const getAvatarColor = (str) => {
        let h = 0;
        for (const c of (str || 'A')) h = (h * 31 + c.charCodeAt(0)) % avatarColors.length;
        return avatarColors[h];
    };

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">

                {/* Toast */}
                {toast && (
                    <div style={{
                        position: 'fixed', top: 20, right: 20, zIndex: 9999,
                        background: toast.type === 'error' ? '#ef4444' : '#22c55e',
                        color: '#fff', padding: '10px 20px', borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: 14,
                        display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <i className={`fas fa-${toast.type === 'error' ? 'times-circle' : 'check-circle'}`}></i>
                        {toast.text}
                    </div>
                )}

                {/* Page Header */}
                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title">WA Contacts</h3>
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
                                <li className="breadcrumb-item active">WA Contacts</li>
                            </ul>
                        </div>
                        <div className="col-auto" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {/* NEW: Update Tags from Sub Industry */}
                            <button
                                className="btn btn-outline-info"
                                onClick={updateTagsFromLeads}
                                disabled={updatingTags}
                                title="Update WA Contact tags using Sub Industry from matched leads"
                            >
                                <i className={`fas fa-${updatingTags ? 'spinner fa-spin' : 'tags'} mr-1`}></i>
                                {updatingTags ? 'Updating...' : 'Sync Tags from Leads'}
                            </button>

                            {/* NEW: Clean Failed Contacts */}
                            <button
                                className="btn btn-outline-danger"
                                onClick={cleanFailedContacts}
                                disabled={cleaningFailed}
                                title="Remove contacts where all outbound messages failed (non-WhatsApp numbers)"
                            >
                                <i className={`fas fa-${cleaningFailed ? 'spinner fa-spin' : 'broom'} mr-1`}></i>
                                {cleaningFailed ? 'Cleaning...' : 'Clean Failed Contacts'}
                            </button>

                            <button
                                className="btn btn-success"
                                onClick={importLeadsToWhatsApp}
                            >
                                <i className="fab fa-whatsapp mr-1"></i>
                                Import Leads to WA Contacts
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                onClick={importFromConversations}
                                disabled={importing}
                            >
                                <i className={`fas fa-${importing ? 'spinner fa-spin' : 'download'} mr-1`}></i>
                                {importing ? 'Importing...' : 'Import from Chats'}
                            </button>
                            <button className="btn btn-primary" onClick={openAdd}>
                                <i className="fas fa-user-plus mr-1"></i> Add Contact
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats row */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card" style={{ borderRadius: 10 }}>
                            <div className="card-body" style={{ padding: '16px 20px' }}>
                                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Total Contacts</div>
                                <div style={{ fontSize: 28, fontWeight: 700 }}>{contacts.length}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card" style={{ borderRadius: 10 }}>
                            <div className="card-body" style={{ padding: '16px 20px' }}>
                                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Active</div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#22c55e' }}>
                                    {contacts.filter(c => c.isActive).length}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card" style={{ borderRadius: 10 }}>
                            <div className="card-body" style={{ padding: '16px 20px' }}>
                                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Inactive</div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#ef4444' }}>
                                    {contacts.filter(c => !c.isActive).length}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card" style={{ borderRadius: 10 }}>
                            <div className="card-body" style={{ padding: '16px 20px' }}>
                                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>Tags</div>
                                <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6' }}>{tags.length}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-3">
                    <div className="col-md-5">
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text bg-white">
                                    <i className="fas fa-search" style={{ color: '#999' }}></i>
                                </span>
                            </div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name or phone..."
                                value={search}
                                onChange={handleSearch}
                            />
                            {search && (
                                <div className="input-group-append">
                                    <button className="btn btn-outline-secondary" onClick={() => { setSearch(''); fetchContacts('', activeTag); }}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {tags.map(tag => (
                            <button
                                key={tag}
                                onClick={() => handleTagFilter(tag)}
                                style={{
                                    padding: '4px 14px', borderRadius: 20, fontSize: 12,
                                    border: '1.5px solid',
                                    borderColor: activeTag === tag ? '#128C7E' : '#e0e0e0',
                                    background: activeTag === tag ? '#e8f5e9' : '#fff',
                                    color: activeTag === tag ? '#128C7E' : '#666',
                                    cursor: 'pointer', fontWeight: activeTag === tag ? 600 : 400,
                                    transition: 'all 0.15s',
                                }}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table */}
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body" style={{ padding: 0 }}>
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th style={{ padding: '14px 20px', fontWeight: 600, fontSize: 13, color: '#555' }}>Contact</th>
                                                <th style={{ padding: '14px 16px', fontWeight: 600, fontSize: 13, color: '#555' }}>Phone</th>
                                                <th style={{ padding: '14px 16px', fontWeight: 600, fontSize: 13, color: '#555' }}>Email</th>
                                                <th style={{ padding: '14px 16px', fontWeight: 600, fontSize: 13, color: '#555' }}>Tags</th>
                                                <th style={{ padding: '14px 16px', fontWeight: 600, fontSize: 13, color: '#555' }}>Status</th>
                                                <th style={{ padding: '14px 16px', fontWeight: 600, fontSize: 13, color: '#555' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                                                        <i className="fas fa-spinner fa-spin mr-2"></i> Loading contacts...
                                                    </td>
                                                </tr>
                                            ) : contacts.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                                                        <div style={{ color: '#999' }}>
                                                            <i className="fas fa-users" style={{ fontSize: 36, marginBottom: 10, display: 'block', opacity: 0.3 }}></i>
                                                            <p style={{ margin: 0, fontSize: 14 }}>No contacts found.</p>
                                                            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#bbb' }}>
                                                                Add a contact or click "Import from Chats"
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                contacts.map(c => {
                                                    const [bg, fg] = getAvatarColor(c.name || c.phone);
                                                    return (
                                                        <tr key={c.id} style={{ opacity: c.isActive ? 1 : 0.55 }}>
                                                            <td style={{ padding: '12px 20px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                                    <div style={{
                                                                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                                                                        background: bg, color: fg,
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        fontWeight: 600, fontSize: 13,
                                                                    }}>
                                                                        {getInitials(c.name || c.phone)}
                                                                    </div>
                                                                    <div>
                                                                        <div style={{ fontWeight: 500, fontSize: 14 }}>{c.name || '—'}</div>
                                                                        {c.notes && (
                                                                            <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>
                                                                                {c.notes.slice(0, 40)}{c.notes.length > 40 ? '…' : ''}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '12px 16px', fontSize: 13, fontFamily: 'monospace' }}>
                                                                {c.phone}
                                                            </td>
                                                            <td style={{ padding: '12px 16px', fontSize: 13, color: '#555' }}>
                                                                {c.email || <span style={{ color: '#ccc' }}>—</span>}
                                                            </td>
                                                            <td style={{ padding: '12px 16px' }}>
                                                                {c.tags ? (
                                                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                                        {c.tags.split(',').map((tag, i) => (
                                                                            <span key={i} style={{
                                                                                background: '#e3f2fd', color: '#1565c0',
                                                                                padding: '2px 8px', borderRadius: 12, fontSize: 11,
                                                                            }}>
                                                                                {tag.trim()}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : <span style={{ color: '#ccc' }}>—</span>}
                                                            </td>
                                                            <td style={{ padding: '12px 16px' }}>
                                                                <span
                                                                    onClick={() => toggleActive(c)}
                                                                    style={{
                                                                        background: c.isActive ? '#dcfce7' : '#fee2e2',
                                                                        color: c.isActive ? '#15803d' : '#dc2626',
                                                                        padding: '3px 12px', borderRadius: 12,
                                                                        fontSize: 12, cursor: 'pointer',
                                                                        fontWeight: 500,
                                                                        transition: 'all 0.15s',
                                                                    }}
                                                                    title="Click to toggle"
                                                                >
                                                                    {c.isActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </td>
                                                            <td style={{ padding: '12px 16px' }}>
                                                                <div style={{ display: 'flex', gap: 6 }}>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => openEdit(c)}
                                                                        title="Edit"
                                                                    >
                                                                        <i className="fas fa-edit"></i>
                                                                    </button>
                                                                    <a
                                                                        href={`/chat?wa_id=${c.phone}`}
                                                                        className="btn btn-sm btn-outline-success"
                                                                        title="Open in Chat"
                                                                    >
                                                                        <i className="fab fa-whatsapp"></i>
                                                                    </a>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => deleteContact(c.id)}
                                                                        title="Delete"
                                                                    >
                                                                        <i className="fas fa-trash"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            {contacts.length > 0 && (
                                <div style={{ padding: '10px 20px', borderTop: '1px solid #f0f0f0', fontSize: 13, color: '#999' }}>
                                    Showing {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                                    {activeTag && ` · filtered by tag: ${activeTag}`}
                                    {search && ` · search: "${search}"`}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <div style={{
                        position: 'fixed', inset: 0,
                        background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <div style={{
                            background: '#fff', borderRadius: 14, width: 500,
                            maxHeight: '88vh', overflow: 'auto',
                            padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
                                <h5 style={{ margin: 0, fontWeight: 700 }}>
                                    {editContact ? 'Edit Contact' : 'Add New Contact'}
                                </h5>
                                <button
                                    onClick={() => setShowModal(false)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#999' }}
                                >
                                    ×
                                </button>
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 500, fontSize: 13 }}>Full Name</label>
                                <input
                                    type="text" className="form-control"
                                    placeholder="e.g. Dhinesh Kumar"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 500, fontSize: 13 }}>
                                    Phone Number <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="text" className="form-control"
                                    placeholder="919876543210"
                                    value={form.phone}
                                    onChange={e => setForm({ ...form, phone: e.target.value })}
                                    disabled={!!editContact}
                                    style={editContact ? { background: '#f9f9f9' } : {}}
                                />
                                <small style={{ color: '#999', fontSize: 11 }}>
                                    Country code + number, no spaces or +. Example: 919876543210
                                </small>
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 500, fontSize: 13 }}>Email</label>
                                <input
                                    type="email" className="form-control"
                                    placeholder="email@example.com"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 500, fontSize: 13 }}>
                                    Tags <small style={{ color: '#999', fontWeight: 400 }}>(comma separated)</small>
                                </label>
                                <input
                                    type="text" className="form-control"
                                    placeholder="lead, vip, customer"
                                    value={form.tags}
                                    onChange={e => setForm({ ...form, tags: e.target.value })}
                                />
                                {tags.length > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {tags.map(tag => (
                                            <span
                                                key={tag}
                                                onClick={() => {
                                                    const current = form.tags.split(',').map(t => t.trim()).filter(Boolean);
                                                    if (!current.includes(tag)) {
                                                        setForm({ ...form, tags: [...current, tag].join(', ') });
                                                    }
                                                }}
                                                style={{
                                                    background: '#e3f2fd', color: '#1565c0',
                                                    padding: '2px 10px', borderRadius: 12,
                                                    fontSize: 11, cursor: 'pointer',
                                                }}
                                            >
                                                + {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label style={{ fontWeight: 500, fontSize: 13 }}>Notes</label>
                                <textarea
                                    className="form-control" rows={3}
                                    placeholder="Any notes about this contact..."
                                    value={form.notes}
                                    onChange={e => setForm({ ...form, notes: e.target.value })}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={saveContact}
                                    disabled={saving}
                                >
                                    {saving
                                        ? <span><i className="fas fa-spinner fa-spin mr-1"></i>Saving...</span>
                                        : editContact ? 'Update Contact' : 'Add Contact'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default WaContacts;